function toFiniteNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}
export function normalizePriceObjectList(priceObject) {
    if (!priceObject)
        return [];
    if (Array.isArray(priceObject)) {
        return priceObject.filter((entry) => Boolean(entry) && typeof entry === 'object');
    }
    if (typeof priceObject === 'object') {
        return [priceObject];
    }
    return [];
}
export function getPrimaryPriceObject(priceObject) {
    const list = normalizePriceObjectList(priceObject);
    return list[0] || null;
}
export function getPriceObjectEstimatedProcessingFee(priceObject, fallback) {
    const list = normalizePriceObjectList(priceObject);
    for (const entry of list) {
        const estimated = toFiniteNumber(entry.estimatedPaymentProcessingFee);
        if (estimated !== undefined)
            return estimated;
        const legacy = toFiniteNumber(entry.paymentProcessingFee);
        if (legacy !== undefined)
            return legacy;
    }
    return toFiniteNumber(fallback) ?? 0;
}
export function getPriceObjectBaseAmount(priceObject) {
    const list = normalizePriceObjectList(priceObject);
    let total = 0;
    let hasValue = false;
    for (const entry of list) {
        const offerPrice = toFiniteNumber(entry.offerPrice);
        const basePrice = toFiniteNumber(entry.basePrice);
        const perTicket = offerPrice ?? basePrice;
        if (perTicket !== undefined) {
            total += perTicket;
            hasValue = true;
        }
    }
    return hasValue ? total : undefined;
}
function getTicketOfferLabel(ticket) {
    const offer = ticket?.offer;
    const name = (typeof offer?.name === 'string' && offer.name) ||
        (typeof ticket?.name === 'string' && ticket.name) ||
        'Standard Admission';
    return name;
}
function getTicketDisplayPrice(ticket) {
    const cost = Number(ticket?.cost);
    return Number.isFinite(cost) ? cost : 0;
}
export function groupCartTicketsForDisplay(tickets) {
    if (!Array.isArray(tickets) || tickets.length === 0)
        return [];
    const groups = new Map();
    for (const ticket of tickets) {
        if (!ticket || typeof ticket !== 'object')
            continue;
        const row = ticket;
        const label = getTicketOfferLabel(row);
        const unitPrice = getTicketDisplayPrice(row);
        const key = `${label}:${unitPrice.toFixed(2)}`;
        const existing = groups.get(key);
        if (existing) {
            existing.quantity += 1;
            existing.subtotal += unitPrice;
        }
        else {
            groups.set(key, {
                label,
                unitPrice,
                quantity: 1,
                subtotal: unitPrice,
            });
        }
    }
    return [...groups.values()];
}
export function hasMultipleCartTicketPriceGroups(tickets) {
    return groupCartTicketsForDisplay(tickets).length > 1;
}
function roundToCents(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
function isComplimentaryTicket(ticket) {
    if (ticket.free === true)
        return true;
    const offer = ticket.offer;
    if (!offer || typeof offer !== 'object')
        return false;
    return offer.freeOffer === true;
}
function matchPricingObjectByCost(pricingObjects, ticketCost) {
    return (pricingObjects.find((po) => Number(po.totalDue) === ticketCost) ||
        pricingObjects.find((po) => Number(po.offerPrice) === ticketCost) ||
        null);
}
function matchPricingObjectForTicket(ticket, pricingObjects) {
    if (!pricingObjects.length || isComplimentaryTicket(ticket))
        return null;
    if (pricingObjects.length === 1)
        return pricingObjects[0];
    const nested = ticket.am_pricing_object;
    const nestedRecord = nested && typeof nested === 'object'
        ? nested
        : null;
    const pricingLevelId = Number(nestedRecord?.id ?? ticket.pricingLevelId ?? ticket.pricing_level_id);
    if (Number.isFinite(pricingLevelId) && pricingLevelId > 0) {
        const byId = pricingObjects.find((po) => Number(po.id) === pricingLevelId);
        if (byId)
            return byId;
    }
    const plName = ticket.PLName || ticket.pricingLevelName || nestedRecord?.name;
    const ticketCost = Number(ticket.cost);
    if (typeof plName === 'string' && plName) {
        const named = pricingObjects.filter((po) => po.name === plName);
        if (named.length === 1)
            return named[0];
        if (named.length > 1) {
            if (Number.isFinite(ticketCost)) {
                const byCost = matchPricingObjectByCost(named, ticketCost);
                if (byCost)
                    return byCost;
            }
            return named[0];
        }
    }
    if (Number.isFinite(ticketCost)) {
        const byCost = matchPricingObjectByCost(pricingObjects, ticketCost);
        if (byCost)
            return byCost;
    }
    return pricingObjects[0];
}
/**
 * Sales tax already baked into each ticket's all-in price.
 * Website event carts leave cart.totalTax at 0 and store the amount on the
 * pricing object, so checkout has to read it from there.
 */
export function sumIncludedSalesTax(tickets, pricingObjects) {
    const rows = Array.isArray(tickets) ? tickets : [];
    const objects = normalizePriceObjectList(pricingObjects);
    if (!rows.length || !objects.length)
        return 0;
    const total = rows.reduce((sum, ticket) => {
        if (!ticket || typeof ticket !== 'object')
            return sum;
        const pricingObject = matchPricingObjectForTicket(ticket, objects);
        if (!pricingObject)
            return sum;
        const tax = toFiniteNumber(pricingObject.taxPerTicket) ??
            toFiniteNumber(pricingObject.salesTax) ??
            0;
        return sum + tax;
    }, 0);
    return roundToCents(total);
}
function toCents(value) {
    return Math.round((value + Number.EPSILON) * 100);
}
function pricingObjectFeeParts(pricingObject) {
    const offerPrice = toFiniteNumber(pricingObject.offerPrice);
    const basePrice = toFiniteNumber(pricingObject.basePrice);
    return {
        ticketPrice: offerPrice ?? basePrice,
        serviceFee: toFiniteNumber(pricingObject.serviceFee) ?? 0,
        facilityFee: toFiniteNumber(pricingObject.ticketFacilityFee) ??
            toFiniteNumber(pricingObject.facilityFee) ??
            0,
        salesTax: toFiniteNumber(pricingObject.taxPerTicket) ??
            toFiniteNumber(pricingObject.salesTax) ??
            0,
        processingFee: toFiniteNumber(pricingObject.paymentProcessingFee) ??
            toFiniteNumber(pricingObject.estimatedPaymentProcessingFee) ??
            0,
        chargeBackProtection: toFiniteNumber(pricingObject.chargeBackProtection) ?? 0,
    };
}
/**
 * Split an all-in event ticket into the face price and fee lines.
 * Returns null unless every paid ticket's parts add back up to its cost,
 * so the modal never lists fees that would double-count the total.
 */
export function getPrimaryTicketFeeBreakdown(tickets, pricingObjects) {
    const rows = Array.isArray(tickets) ? tickets : [];
    const objects = normalizePriceObjectList(pricingObjects);
    if (!rows.length || !objects.length)
        return null;
    const groups = new Map();
    const totals = {
        serviceFee: 0,
        facilityFee: 0,
        salesTax: 0,
        processingFee: 0,
        chargeBackProtection: 0,
    };
    let paidCount = 0;
    for (const ticket of rows) {
        if (!ticket || typeof ticket !== 'object')
            return null;
        const row = ticket;
        const label = getTicketOfferLabel(row);
        const cost = toFiniteNumber(row.cost) ?? 0;
        if (isComplimentaryTicket(row) || cost <= 0) {
            const key = `${label}:0.00`;
            const existing = groups.get(key);
            if (existing) {
                existing.quantity += 1;
            }
            else {
                groups.set(key, {
                    label,
                    unitPrice: 0,
                    quantity: 1,
                    subtotal: 0,
                });
            }
            continue;
        }
        const pricingObject = matchPricingObjectForTicket(row, objects);
        if (!pricingObject)
            return null;
        const parts = pricingObjectFeeParts(pricingObject);
        if (parts.ticketPrice === undefined)
            return null;
        const summed = roundToCents(parts.ticketPrice +
            parts.serviceFee +
            parts.facilityFee +
            parts.salesTax +
            parts.processingFee +
            parts.chargeBackProtection);
        if (toCents(summed) !== toCents(cost))
            return null;
        paidCount += 1;
        totals.serviceFee += parts.serviceFee;
        totals.facilityFee += parts.facilityFee;
        totals.salesTax += parts.salesTax;
        totals.processingFee += parts.processingFee;
        totals.chargeBackProtection += parts.chargeBackProtection;
        const unitPrice = roundToCents(parts.ticketPrice);
        const key = `${label}:${unitPrice.toFixed(2)}`;
        const existing = groups.get(key);
        if (existing) {
            existing.quantity += 1;
            existing.subtotal = roundToCents(existing.subtotal + unitPrice);
        }
        else {
            groups.set(key, {
                label,
                unitPrice,
                quantity: 1,
                subtotal: unitPrice,
            });
        }
    }
    const hasItemizedAmount = totals.serviceFee > 0 ||
        totals.facilityFee > 0 ||
        totals.salesTax > 0 ||
        totals.processingFee > 0 ||
        totals.chargeBackProtection > 0;
    if (!paidCount || !hasItemizedAmount)
        return null;
    return {
        groups: [...groups.values()],
        serviceFee: roundToCents(totals.serviceFee),
        facilityFee: roundToCents(totals.facilityFee),
        salesTax: roundToCents(totals.salesTax),
        processingFee: roundToCents(totals.processingFee),
        chargeBackProtection: roundToCents(totals.chargeBackProtection),
    };
}
export function aggregatePriceObjectDisplayTotals(priceObject) {
    const list = normalizePriceObjectList(priceObject);
    const totals = {
        offerPrice: 0,
        basePrice: 0,
        serviceFee: 0,
        facilityFee: 0,
        salesTax: 0,
        chargeBackProtection: 0,
        paymentProcessingFee: 0,
        estimatedPaymentProcessingFee: 0,
        ticketCostWithFeesAndTax: 0,
    };
    if (!list.length)
        return totals;
    for (const entry of list) {
        totals.offerPrice += toFiniteNumber(entry.offerPrice) ?? 0;
        totals.basePrice += toFiniteNumber(entry.basePrice) ?? 0;
        totals.serviceFee += toFiniteNumber(entry.serviceFee) ?? 0;
        totals.facilityFee +=
            toFiniteNumber(entry.ticketFacilityFee) ??
                toFiniteNumber(entry.facilityFee) ??
                0;
        totals.salesTax +=
            toFiniteNumber(entry.taxPerTicket) ?? toFiniteNumber(entry.salesTax) ?? 0;
        totals.chargeBackProtection +=
            toFiniteNumber(entry.chargeBackProtection) ?? 0;
        totals.paymentProcessingFee +=
            toFiniteNumber(entry.paymentProcessingFee) ?? 0;
        totals.estimatedPaymentProcessingFee +=
            toFiniteNumber(entry.estimatedPaymentProcessingFee) ?? 0;
        totals.ticketCostWithFeesAndTax +=
            toFiniteNumber(entry.ticketCostWithFeesAndTax) ?? 0;
    }
    return totals;
}
